import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import { useNavigate } from 'react-router-dom';
import SmsService from '../../../services/SmsService';

interface AdminProfile {
  uid: string;
  name: string;
  email: string;
  organization: string;
  phoneNumber: string;
  userLevel: number;
  permissions: {
    users: boolean;
    facilities: boolean;
    facilityReservations: boolean;
    portville: boolean;
    notices: boolean;
  };
}

const SettingsPage: React.FC = () => {
  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { user, userLevel, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();
  
  // SMS 인증 관련 상태
  const [sendingSms, setSendingSms] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerificationInput, setShowVerificationInput] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [verificationSuccess, setVerificationSuccess] = useState<string | null>(null);
  const [remainingTime, setRemainingTime] = useState(0);

  useEffect(() => {
    if (!authLoading && user) {
      const fetchProfile = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'user_profiles', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setProfile({
              uid: userDoc.id,
              name: data.name || '',
              email: data.email || '',
              organization: data.organization || '',
              phoneNumber: data.phoneNumber || '',
              userLevel: data.userLevel || 0,
              permissions: data.permissions || {}
            });
          }
        } catch (error) {
          console.error('프로필 조회 실패:', error);
          setError('프로필 정보를 불러오는데 실패했습니다.');
        } finally {
          setLoading(false);
        }
      };

      fetchProfile();
    }
  }, [user, authLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const userRef = doc(db, 'user_profiles', profile.uid);
      await updateDoc(userRef, {
        name: profile.name,
        organization: profile.organization,
        phoneNumber: profile.phoneNumber,
        updatedAt: new Date().toISOString()
      });

      setSuccess('프로필 정보가 성공적으로 업데이트되었습니다.');
    } catch (error) {
      console.error('프로필 업데이트 실패:', error);
      setError('프로필 정보 업데이트에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!profile) return;
    const { name, value } = e.target;
    setProfile(prev => prev ? { ...prev, [name]: value } : null);
  };

  // SMS 인증번호 발송
  const handleSendVerification = async () => {
    if (!profile?.phoneNumber) {
      setVerificationError('전화번호를 입력해주세요.');
      return;
    }

    setSendingSms(true);
    setVerificationError(null);
    setVerificationSuccess(null);

    try {
      const { requestId } = await SmsService.sendVerificationCode(profile.phoneNumber);
      
      setShowVerificationInput(true);
      setVerificationSuccess('인증번호가 발송되었습니다. 3분 이내에 입력해주세요.');
      
      // 타이머 시작
      setRemainingTime(180); // 3분 = 180초
      startTimer();
      
    } catch (error) {
      console.error('SMS 발송 실패:', error);
      setVerificationError(error instanceof Error ? error.message : 'SMS 발송에 실패했습니다.');
    } finally {
      setSendingSms(false);
    }
  };

  // 인증번호 검증
  const handleVerifyCode = async () => {
    if (!profile?.phoneNumber || !verificationCode) {
      setVerificationError('인증번호를 입력해주세요.');
      return;
    }

    try {
      const isValid = await SmsService.verifyCode(profile.phoneNumber, verificationCode);
      
      if (isValid) {
        setVerificationSuccess('전화번호 인증이 완료되었습니다.');
        setShowVerificationInput(false);
        setVerificationCode('');
        setRemainingTime(0);
      } else {
        setVerificationError('인증번호가 올바르지 않거나 만료되었습니다.');
      }
    } catch (error) {
      console.error('인증번호 확인 실패:', error);
      setVerificationError(error instanceof Error ? error.message : '인증번호 확인에 실패했습니다.');
    }
  };

  // 타이머 시작
  const startTimer = () => {
    const timer = setInterval(() => {
      setRemainingTime(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setShowVerificationInput(false);
          setVerificationError('인증 시간이 만료되었습니다.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 시간 포맷 (mm:ss)
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">로딩 중...</div>
      </div>
    );
  }

  if (!user || !profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">접근 권한이 없습니다.</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">개인정보 설정</h2>
      
      <div className="bg-white rounded-lg shadow-sm p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
              {success}
            </div>
          )}

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              이름
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={profile.name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              이메일
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={profile.email}
              disabled
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm"
            />
            <p className="mt-1 text-sm text-gray-500">
              이메일은 변경할 수 없습니다.
            </p>
          </div>

          <div>
            <label htmlFor="organization" className="block text-sm font-medium text-gray-700">
              소속
            </label>
            <input
              type="text"
              id="organization"
              name="organization"
              value={profile.organization}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
              전화번호
            </label>
            <div className="mt-1 flex gap-2">
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={profile.phoneNumber}
                onChange={handleChange}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
              <button
                type="button"
                onClick={handleSendVerification}
                disabled={sendingSms || !profile.phoneNumber}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {sendingSms ? '발송 중...' : '인증'}
              </button>
            </div>
          </div>

          {/* SMS 인증 관련 메시지 */}
          {verificationError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {verificationError}
            </div>
          )}
          
          {verificationSuccess && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
              {verificationSuccess}
            </div>
          )}

          {/* 인증번호 입력 필드 */}
          {showVerificationInput && (
            <div>
              <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700">
                인증번호
              </label>
              <div className="mt-1 flex gap-2">
                <input
                  type="text"
                  id="verificationCode"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="인증번호 6자리를 입력하세요"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  maxLength={6}
                />
                <button
                  type="button"
                  onClick={handleVerifyCode}
                  disabled={!verificationCode || verificationCode.length !== 6}
                  className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  확인
                </button>
              </div>
              {remainingTime > 0 && (
                <p className="mt-1 text-sm text-gray-500">
                  남은 시간: {formatTime(remainingTime)}
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">
              관리자 등급
            </label>
            <div className="mt-1 text-sm text-gray-900">
              {profile.userLevel === 1 ? '최고관리자' : '일반관리자'}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {saving ? '저장 중...' : '저장하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SettingsPage;
